import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { RegisterRequest } from '../../dtos/register-request';
import { AuthService } from '../../services/auth.service';
import { ToastService } from '../../services/toast.service';
import { AdminUserService } from '../../services/admin-user.service';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  standalone: false,
})
export class RegisterComponent implements OnInit {
  registerForm: UntypedFormGroup;
  submitted = false;
  error = false;
  errorMessage = '';
  success = false;
  successMessage = '';
  showSuperAdminSetupLink = false;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private authService: AuthService,
    private adminUserService: AdminUserService,
    private toastService: ToastService,
    private router: Router,
  ) {
    this.registerForm = this.formBuilder.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      affiliation: [''],
      password: ['', [Validators.required, Validators.minLength(8)]],
    });
  }

  ngOnInit(): void {
    this.adminUserService.isSuperAdminSetupAvailable().subscribe({
      next: (available) => {
        this.showSuperAdminSetupLink = available;
      },
      error: () => {
        this.showSuperAdminSetupLink = false;
      },
    });
  }

  registerUser() {
    this.submitted = true;
    this.error = false;
    this.success = false;

    if (!this.registerForm.valid) {
      return;
    }

    const registerRequest = new RegisterRequest(
      this.registerForm.controls.email.value,
      this.registerForm.controls.password.value,
      this.registerForm.controls.firstName.value,
      this.registerForm.controls.lastName.value,
      this.registerForm.controls.affiliation.value,
    );

    this.authService.registerUser(registerRequest).subscribe({
      next: () => {
        this.success = true;
        this.successMessage =
          'Account created. Please check your email and verify your account before logging in. Shortly redirecting to login...';
        setTimeout(() => {
          this.router.navigate(['/login']);
        }, 5000);
        this.submitted = false;
      },
      error: (error) => {
        this.error = true;
        this.errorMessage = this.parseRegisterError(error);
        this.toastService.error(
          'Registration Failed',
          this.errorMessage || 'Could not create your account',
        );
      },
    });
  }

  vanishError() {
    this.error = false;
  }

  /**
   * Extracts a natural-language message from a registration error. The request uses
   * responseType 'text', so `error.error` is an unparsed JSON string; the previous code fell
   * through to rendering that raw JSON. Returns the ProblemDetail `detail`, the joined bean
   * validation messages, or a friendly fallback (never raw JSON).
   */
  private parseRegisterError(error: any): string {
    if (error?.status === 0) {
      return 'Cannot reach the server. Please check your connection and try again.';
    }
    let body = error?.error;
    if (typeof body === 'string') {
      try {
        body = JSON.parse(body);
      } catch {
        return body || 'Registration failed.';
      }
    }
    if (body && typeof body === 'object') {
      if (typeof body.detail === 'string') {
        return body.detail;
      }
      const validationErrors = body['Validation errors'];
      if (Array.isArray(validationErrors) && validationErrors.length > 0) {
        return validationErrors.join('. ');
      }
      if (typeof body.error === 'string') {
        return body.error;
      }
    }
    return 'Registration failed.';
  }
}
